import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { QRCodeSVG } from 'qrcode.react';
import { useTranslation } from 'react-i18next';
import { authApi } from '../axiosInstance';

function NewSurveyPage({ user }) {
  const { t } = useTranslation();
  const navigate = useNavigate();

  function extractCustomerId(src) {
    const candidates = [
      src?.customer_id,
      src?.customerId,
      src?.client_id,
      src?.clientId,
      src?.tenant_id,
      src?.tenantId,
      src?.user?.customer_id,
      src?.user?.customerId,
      src?.user?.client_id,
      src?.user?.clientId,
      src?.user?.tenant_id,
      src?.user?.tenantId,
    ];

    for (const v of candidates) {
      const n = Number(v);
      if (Number.isInteger(n) && n > 0) return n;
    }
    return null;
  }

  const [customerId, setCustomerId] = useState(() => extractCustomerId(user));
  const [resolvingCustomerId, setResolvingCustomerId] = useState(false);

  const [title, setTitle] = useState('');
  const [description, setDescription] = useState(t('newSurvey.defaultDescription'));
  const [questions, setQuestions] = useState([]);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const [surveyLink, setSurveyLink] = useState('');

  useEffect(() => {
    let alive = true;

    async function resolveCid() {
      if (customerId) return;

      setResolvingCustomerId(true);
      try {
        // FIX: whoami NICHT über authApi, sondern über denselben Root-Endpoint wie der Rest der App
        const who = await axios.get('/whoami', { withCredentials: true });
        const cid = extractCustomerId(who?.data);
        if (alive && cid) setCustomerId(cid);
      } catch (e) {
        // bewusst still
      } finally {
        if (alive) setResolvingCustomerId(false);
      }
    }

    resolveCid();
    return () => {
      alive = false;
    };
  }, [customerId, user]);

  const defaultQuestions = [
    { question_text: t('newSurvey.defaultQ1'), question_type: 'scale_5' },
    { question_text: t('newSurvey.defaultQ2'), question_type: 'scale_5' },
    { question_text: t('newSurvey.defaultQ3'), question_type: 'scale_5' },
    { question_text: t('newSurvey.defaultQ4'), question_type: 'scale_5' },
    { question_text: t('newSurvey.defaultQ5'), question_type: 'scale_5' },
    { question_text: t('newSurvey.defaultQ6'), question_type: 'scale_5' },
    { question_text: t('newSurvey.defaultQ7'), question_type: 'scale_5' },
    { question_text: t('newSurvey.defaultQ8'), question_type: 'scale_5' },
    { question_text: t('newSurvey.defaultQ9'), question_type: 'scale_5' },
    { question_text: t('newSurvey.defaultQ10'), question_type: 'scale_5' },
    { question_text: t('newSurvey.defaultQ11'), question_type: 'open' },
    { question_text: t('newSurvey.defaultQ12'), question_type: 'open' }
  ];

  const forcedQuestion = {
    question_text: t('newSurvey.forcedQuestion'),
    question_type: 'radio'
  };

  function handleLoadDefaults() {
    setQuestions(defaultQuestions);
  }

  function addQuestion() {
    setQuestions(prev => [...prev, { question_text: '', question_type: 'scale_5' }]);
  }

  function handleQuestionChange(index, field, value) {
    const updated = [...questions];
    updated[index][field] = value;
    setQuestions(updated);
  }

  function handleDeleteQuestion(index) {
    const updated = [...questions];
    updated.splice(index, 1);
    setQuestions(updated);
  }

  async function handleSave(e) {
    e.preventDefault();
    setError('');
    setMessage('');
    setSurveyLink('');

    const cid = customerId ?? extractCustomerId(user);

    if (!cid) {
      setError(t('newSurvey.noCustomerId', 'Keine customer_id im Benutzerprofil vorhanden.'));
      return;
    }

    if (questions.length === 0) {
      setError(t('newSurvey.noQuestions'));
      return;
    }

    const finalQuestions = [...questions, forcedQuestion];

    try {
      const res = await axios.post('/survey/surveys', {
        customer_id: cid,
        title,
        description,
        questions: finalQuestions
      }, { withCredentials: true });

      const sid = res.data.survey_id;
      setMessage(t('newSurvey.created') + sid);

      const link = `https://localhost:3003/#/employee/survey/${sid}`;
      setSurveyLink(link);
    } catch (err) {
      setError(err.response?.data || t('newSurvey.saveError'));
    }
  }

  return (
    <div className="min-h-screen bg-black text-white p-6">
      <div className="flex justify-start mb-6">
        <button
          onClick={() => navigate('/hr/overview')}
          className="flex items-center gap-2 bg-mentixxgreen text-black font-bold py-2 px-4 rounded-2xl hover:bg-green-400 transition"
        >
          {t('newSurvey.backToDashboard')}
        </button>
      </div>

      <h2 className="text-2xl font-bold mb-6">{t('newSurvey.heading')}</h2>

      {error && <p className="text-red-500 mb-4">{error}</p>}
      {message && <p className="text-green-400 mb-4">{message}</p>}

      <div className="flex gap-12 items-start">
        {/* Linke Spalte: Fragenkatalog */}
        <form
          onSubmit={handleSave}
          className="grid gap-4 max-w-3xl bg-gray-800 p-6 rounded-2xl shadow-md flex-1"
        >
          <div>
            <label className="block mb-1 font-semibold">{t('newSurvey.projectTitle')}</label>
            <textarea
              rows={2}
              className="w-full p-2 bg-gray-700 rounded text-white"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
            />
          </div>

          <div>
            <label className="block mb-1 font-semibold">{t('newSurvey.projectDescription')}</label>
            <textarea
              rows={3}
              className="w-full p-2 bg-gray-700 rounded text-white"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
            />
          </div>

          <div>
            <p className="font-semibold mb-2">{t('newSurvey.editableHint')}</p>
            {questions.map((q, i) => (
              <div key={i} className="mb-4 bg-gray-700 p-4 rounded">
                <textarea
                  rows={2}
                  className="w-full p-2 bg-gray-600 rounded text-white"
                  placeholder={t('newSurvey.questionPlaceholder')}
                  value={q.question_text}
                  onChange={(e) => handleQuestionChange(i, 'question_text', e.target.value)}
                />
                <select
                  className="w-full p-2 mt-2 bg-gray-600 rounded text-white"
                  value={q.question_type}
                  onChange={(e) => handleQuestionChange(i, 'question_type', e.target.value)}
                >
                  <option value="scale_5">{t('newSurvey.scale5')}</option>
                  <option value="open">{t('newSurvey.open')}</option>
                  <option value="radio">{t('newSurvey.radio')}</option>
                  <option value="checkbox">{t('newSurvey.checkbox')}</option>
                </select>
                <button
                  type="button"
                  className="mt-2 bg-red-600 hover:bg-red-700 text-white py-1 px-2 rounded"
                  onClick={() => handleDeleteQuestion(i)}
                >
                  {t('newSurvey.deleteQuestion')}
                </button>
              </div>
            ))}

            <div className="flex gap-4">
              <button
                type="button"
                onClick={addQuestion}
                className="bg-mentixxgreen hover:bg-green-400 text-black font-bold py-2 px-4 rounded-2xl transition"
              >
                {t('newSurvey.addQuestion')}
              </button>

              <button
                type="button"
                onClick={handleLoadDefaults}
                className="bg-gray-600 hover:bg-gray-500 text-white font-bold py-2 px-4 rounded-2xl transition"
              >
                {t('newSurvey.loadDefaults')}
              </button>
            </div>
          </div>

          <div className="mt-6 p-4 bg-gray-700 rounded">
            <p className="font-bold">{t('newSurvey.forcedLabel')}</p>
            <p className="mt-2">{forcedQuestion.question_text}</p>
            <p className="italic text-sm mt-2">{t('newSurvey.forcedHint')}</p>
          </div>

          <button
            type="submit"
            className="mt-6 bg-mentixxgreen hover:bg-green-400 text-black font-bold py-2 px-6 rounded-2xl transition"
            disabled={resolvingCustomerId}
          >
            {t('newSurvey.save')}
          </button>
        </form>

        {/* Rechte Spalte: QR/Link-Box */}
        {surveyLink && (
          <div className="w-[320px] min-w-[260px] bg-gray-800 rounded-2xl p-6 flex flex-col items-center shadow-lg ml-2">
            <div className="text-lg font-semibold mb-4 text-white text-center">{t('newSurvey.linkLabel')}</div>
            <a href={surveyLink} className="text-green-400 break-all text-center mb-4">{surveyLink}</a>
            <div>
              <QRCodeSVG value={surveyLink} size={148} />
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default NewSurveyPage;
